// The card's aggregator: the period's scanned games that reached an ending,
// by their ending result (`GameFacts.endingResult`: Won, Comeback, Drawn,
// Blown win, Lost — from how the ending started and how the game ended),
// with each result's start × outcome cells for the tooltips. Also the
// period's games, for the card's scope note. Registered with the game store
// by this plugin's setup; the store runs it over the last 30 days' games on
// every change and keeps the result.
import { aggregatorId, ENDING_RESULTS, type Accumulator, type Aggregator, type EndingResult, type EndingStart, type GameOutcome } from '@chess/api-model';

/** One start × outcome cell of a result, with its games. */
export interface EndingCell {
  start: EndingStart;
  outcome: GameOutcome;
  count: number;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface EndingResultsResult {
  /** Every game of the period, for "N of M games reached an ending". */
  games: number;
  /** Games in each result; they add up to the endings counted. */
  byResult: Record<EndingResult, number>;
  /** Each result's cells with games, in grid order (start winning → lost, outcome win → loss). */
  cells: Record<EndingResult, EndingCell[]>;
}

const STARTS: readonly EndingStart[] = ['winning', 'level', 'lost'];
const OUTCOMES: readonly GameOutcome[] = ['win', 'draw', 'loss'];

export class EndingResultsAggregator implements Aggregator<EndingResultsResult> {
  readonly id = aggregatorId('ending-results');

  start(): Accumulator<EndingResultsResult> {
    const byResult = { won: 0, comeback: 0, drawn: 0, blown: 0, lost: 0 } satisfies Record<EndingResult, number>;
    const grid = new Map<string, { result: EndingResult; count: number }>();
    let games = 0;
    return {
      add: (game) => {
        games += 1;
        if (!game.scanned || !game.endingResult || !game.endingStart) return;
        byResult[game.endingResult] += 1;
        const key = `${game.endingStart}:${game.outcome}`;
        const cell = grid.get(key) ?? { result: game.endingResult, count: 0 };
        cell.count += 1;
        grid.set(key, cell);
      },
      result: () => {
        const cells: Record<EndingResult, EndingCell[]> = { won: [], comeback: [], drawn: [], blown: [], lost: [] };
        for (const start of STARTS)
          for (const outcome of OUTCOMES) {
            const cell = grid.get(`${start}:${outcome}`);
            if (cell) cells[cell.result].push({ start, outcome, count: cell.count });
          }
        return { games, byResult, cells };
      },
    };
  }
}

/** The games that reached an ending (with a start read): every result together. */
export const endingsOf = (r: EndingResultsResult) => ENDING_RESULTS.reduce((sum, k) => sum + r.byResult[k], 0);
